import axios from "axios";

import { toDateOnly } from "../core/dateOnly";

// ─── API layer for the purpose-built listing flows (New Stock / New
//     Consignment). Everything here is store-scoped server-side via the
//     tenant context — no store ids are passed from the client. ───────────────

/**
 * Upload one file (pdf or image) to the generic file store.
 * Same endpoint the Upload Invoice tab uses (POST /api/files, multipart).
 * Returns { id, name } or throws.
 */
export const uploadIntakeFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post("/api/files", fd);
  return { id: data.id, name: data.original_name || file.name };
};

/**
 * Image-recognition analysis of the rep's intake documents.
 *
 * Backend contract (App\Services\ListingIntakeAnalyzer):
 *   POST /api/listings/intake/analyze
 *   body: { kind: "supplier_invoice" | "watch_images", file_ids: [int, ...] }
 *   200:  {
 *     users:   [{ id, first_name, last_name, email, phone }],   // existing store users matched from the document
 *     product: { id?, reference_number?, brand?, model? },      // id only on an exact reference match
 *     fields:  { serial_number?, warranty_date?, buying_price?,  // major units, like the form
 *                buying_currency?, year?, supplier_name? }
 *   }
 *   503:  the documents could not be read (model down, or a file type it cannot take).
 *
 * Suggestions only — nothing is created or written server-side, so a wrong read
 * costs one ignored chip. Any failure (503, 404 on an older backend, network)
 * resolves to null and the flows degrade to fully manual: no chips, no auto-pick.
 * A 200 with empty structures is different and deliberate — the documents WERE
 * read and held nothing worth suggesting.
 */
export const analyzeIntakeDocuments = async (kind, fileIds) => {
  try {
    const { data } = await axios.post("/api/listings/intake/analyze", {
      kind,
      file_ids: fileIds,
    });
    return data || null;
  } catch (e) {
    return null;
  }
};

/**
 * Store-wide Sell Request search — by client name, email, phone, brand,
 * model, or reference. POST /api/user/{userId}/requests/sell with
 * filters.search: for an ADMIN caller the backend skips the own-requests
 * constraint and returns every sell request in the active store
 * (RequestService::getList — admin path + applyStoreIdMConstraint), which is
 * exactly the store-wide behaviour these flows need. `userId` must therefore
 * be the LOGGED-IN user's id, not a client id.
 */
export const searchSellRequests = async (userId, query) => {
  const { data } = await axios.post(`/api/user/${userId}/requests/sell`, {
    filters: { ...(query ? { search: query } : {}) },
    paging: { offset: 0, limit: 12 },
    sort: { field: "created_at", direction: "desc" },
  });
  return data?.items ?? [];
};

/** Full request payload — includes client, product, xero_purchase_order_file_url. */
export const fetchRequest = async (requestId) => {
  const { data } = await axios.get(`/api/requests/${requestId}`);
  return data;
};

/** Full product payload (EAV) — what RCProductSelector hands to the form. */
export const fetchProduct = async (productId) => {
  const { data } = await axios.get(`/api/products/${productId}`);
  return data;
};

/**
 * Resolve an AI product suggestion to a full catalog product:
 * direct id when Gemini matched one, else first hit of the same catalog
 * search the product selector uses (reference / brand / model text).
 * Returns the FULL product payload or null.
 */
export const resolveSuggestedProduct = async (suggestion) => {
  if (!suggestion) return null;
  try {
    if (suggestion.id) return await fetchProduct(suggestion.id);
    const query = [suggestion.reference_number, suggestion.brand, suggestion.model]
      .filter(Boolean)
      .join(" ")
      .trim();
    if (!query) return null;
    const { data } = await axios.post("/api/products/search", {
      list: "default",
      query,
      filters: { query },
      paging: { offset: 0, limit: 1 },
    });
    const hit = data?.items?.[0];
    return hit?.id ? await fetchProduct(hit.id) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Purchasing / consignment agreement PDFs for a Sell Request.
 * These endpoints GENERATE the pdf when none exists yet and stamp
 * *_agreement_sent_at on first generation — so they are called lazily, on an
 * explicit user action in the CONTRACTS section, never automatically.
 * Both return { file_id, signed_url }.
 */
export const fetchPurchasingAgreement = async (requestId) => {
  const { data } = await axios.get(`/api/requests/${requestId}/pdf-purchase`);
  return data;
};
export const fetchConsignmentAgreement = async (requestId) => {
  const { data } = await axios.get(`/api/requests/${requestId}/pdf-consign`);
  return data;
};

/**
 * Generate the Sell Request's Xero purchase order (a REAL purchase order in
 * the pinned Xero org — priced from the request's last quote). The backend
 * 422s with a human message when no Xero org is pinned on the request. This
 * only creates and returns the PDF; emailing it is the separate, always-manual
 * POST /requests/{id}/send-purchase-order/{pdfId} — never called from here.
 * Returns the updated request fields incl. xero_purchase_order_file_id/_url.
 */
export const generatePurchaseOrder = async (requestId) => {
  const { data } = await axios.post(`/api/requests/${requestId}/pdf-purchase-order`, {});
  return data;
};

/**
 * Map a selected Sell Request onto listing-form prefills (one-way populate;
 * the 2-way sync of costs/serial/dates back onto the request is a backend
 * follow-up). Money comes off the API in pence — the form holds major units
 * (ListingHelper.processSubmitValues multiplies by 100 on submit).
 *
 *   Listings:         condition, box (delivery set), year   — from Confirm Request
 *   Internal Details: serial number, buying price (last quote → quoted price →
 *                     price expectation), buying currency, supplier payment
 *                     date (date of purchase), cost fields when present
 */
export const listingPrefillFromRequest = (request) => {
  if (!request) return {};

  // A dropdown value that isn't one of the option ids renders as "—" while
  // still sitting in the payload, so anything unrecognised is dropped rather
  // than half-applied. Condition ids come from DaoWatchesFields, delivery-set
  // ids from product/constants boxOptions — both are the listing form's own
  // vocabularies, and a request stores the same strings.
  const CONDITION_IDS = ["new", "excellent", "very-good", "good", "fair"];
  const BOX_IDS = ["watch-box-papers", "watch-only", "watch-papers", "watch-box"];

  const slug = (v) =>
    typeof v === "string" ? v.trim().toLowerCase().replace(/[\s_]+/g, "-") : undefined;
  // `condition` is an array on a request (validated `array|nullable`), a plain
  // string on a listing.
  const normalizeCondition = (c) => {
    const v = slug(Array.isArray(c) ? c[0] : c);
    return CONDITION_IDS.includes(v) ? v : undefined;
  };
  const normalizeBox = (b) => {
    const v = slug(b);
    return BOX_IDS.includes(v) ? v : undefined;
  };
  const pence = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n / 100 : undefined;
  };

  const buyingPence =
    request.last_quote?.price ?? request.quoted_price ?? request.price_expectation;

  return {
    condition: normalizeCondition(request.condition),
    box: normalizeBox(request.delivery_set),
    year: request.year || undefined,
    serial_number: request.serial_number || undefined,
    buying_price: pence(buyingPence),
    buying_currency: request.currency ? String(request.currency).toLowerCase() : undefined,
    // Date-only: the request carries a Laravel datetime and these feed DATE
    // columns, so trim the time part here rather than letting it reach the API.
    //
    // These are two different dates and used to be conflated: the request's
    // purchase date was written into supplier_payment_date, because the listing
    // form had no date_purchase field to put it in. It has one now, so each
    // lands where it belongs — when the watch was bought, and when the supplier
    // was paid for it.
    date_purchase: toDateOnly(request.date_purchase) || undefined,
    supplier_payment_date: toDateOnly(request.supplier_payment_date) || undefined,
    // Cost fields only exist on some requests — populate defensively.
    costs_currency: request.costs_currency ? String(request.costs_currency).toLowerCase() : undefined,
    polishing: pence(request.polishing),
    accessories: pence(request.accessories),
    shipping: pence(request.shipping),
    travel: pence(request.travel),
  };
};

/**
 * End-of-flow Sell Request creation (New Stock via supplier invoice, and
 * New Consignment "New Request"): a sell request for the assigned
 * seller/consignor, linked to the freshly created listing, moved straight to
 * its terminal status.
 *
 * "Fully done" does not exist as a request status today — 'done' is the
 * terminal state (Request::STATUS_DONE). We create the request and then set
 * status via the dedicated admin endpoint. (A one-field PUT /api/requests/{id}
 * would wipe year/price_expectation — the update endpoint rebuilds the whole
 * record — so status goes through PUT .../admin instead.)
 */
export const createDoneSellRequest = async ({ clientId, productId, listingId, isConsignment }) => {
  // product_id is REQUIRED whenever type is 'product' — omitting it is a 422
  // ("The product id field is required when type is product"), which is what
  // happened while the flows only knew about products they had auto-picked
  // themselves and not the one chosen in the form's own product selector.
  if (!productId) {
    throw new Error("A product must be selected before a Sell Request can be created.");
  }

  const { data: request } = await axios.post("/api/requests", {
    request_type: "sell",
    type: "product",
    client_id: clientId,
    product_id: productId,
    // Only when the listing already exists. During a create flow it does not
    // yet, so the link is made afterwards via attachListingToRequest.
    ...(listingId ? { linked_listing_id: listingId } : {}),
    ...(isConsignment ? { is_stock: false } : {}),
  });
  // The request EXISTS from here on. If only the status step fails, return it
  // anyway so the caller still links it — throwing here showed "could not be
  // created" and a retry made a duplicate Sell Request. It stays in its
  // initial status and can be moved on the request itself.
  try {
    await axios.put(`/api/requests/${request.id}/admin`, { status: "done" });
  } catch (error) {
    console.warn(`Sell Request #${request.id} created but not marked done`, error);
    return { ...request, statusError: true };
  }
  return request;
};

/**
 * Point an existing request at a listing (requests.linked_listing_id).
 *
 * Needed because a request created mid-flow exists BEFORE the listing does, and
 * because a listing created without one (older listings, CSV imports) can be
 * linked from the edit screen. Deliberately not a PUT /api/requests/{id}: that
 * endpoint rebuilds the record from the payload, so a one-field body wipes
 * year / price_expectation.
 */
export const attachListingToRequest = async (requestId, listingId) => {
  const { data } = await axios.post(`/api/requests/${requestId}/attach-listing`, {
    listing_id: listingId,
  });
  return data;
};

/**
 * What a consignment save owes its Sell Request: "attach" an existing one,
 * "create" the consignor's, or "none".
 *
 * A rule rather than a branch in the page because both mistakes it rules out are
 * invisible until a save runs:
 *  - TWO requests for one listing. `pendingAttachId` cannot carry the rule on
 *    its own — unlinking clears it, and the create branch would then fire for a
 *    consignor who already has a request. `createdRequestId` latches instead:
 *    it records the request the flow itself raised and is never cleared, so a
 *    save after an unlink links nothing rather than creating a duplicate.
 *  - An existing request that never gets linked. Selecting one arms
 *    `pendingAttachId`, so "attach" is reachable from the search as well as
 *    from a request created mid-flow.
 *
 * `option` is the ASSIGN CONSIGNOR route: every route except "existing" ends by
 * raising the consignor's own request.
 */
export const sellRequestActionOnSave = ({ option, pendingAttachId, createdRequestId }) => {
  if (pendingAttachId) return "attach";
  if (option && option !== "existing" && !createdRequestId) return "create";
  return "none";
};

/** Activate a saved listing (Publish Listing button). */
export const activateListing = (listingId) =>
  axios.put(`/api/listings/${listingId}/active`, { active: true });

/**
 * Legacy marker for "In Repair".
 *
 * Repairs now live in `listings.is_repair`. Before that column existed, CREATE
 * REPAIR stamped this marker into the internal note and the card read it back —
 * so it is still honoured on READ (below), and the In Repair toggle strips it
 * when a repair is marked finished. Nothing writes it any more.
 */
export const IN_REPAIR_MARKER = "[In Repair]";
export const isInRepair = (listing) =>
  !!listing?.is_repair ||
  (listing?.private_note || "").toLowerCase().includes(IN_REPAIR_MARKER.toLowerCase());

/**
 * Where the intake section's Continue should hand over to.
 *
 * A walk-in whose pictures were read, or an existing Sell Request, already
 * carries a product — the rep can go straight to LISTINGS. A consignor picked
 * with no pictures does NOT, and handing over to LISTINGS there silently skips
 * SELECT PRODUCT and leaves the rep filling in a listing with no product
 * attached (reported from staging 2026-08-28).
 *
 * @param {{ product?: {id?: number|string}|null }} state
 * @returns {"listings"|"product"}
 */
export const continueTargetAfterIntake = ({ product } = {}) =>
  product?.id ? "listings" : "product";
